'use client';

/**
 * Document-scoped first-mention de-dupe for entity @mentions.
 *
 * When the SAME entity is mentioned many times in ONE piece, the rich image-chip
 * (TagMention) repeating on every mention is noisy. This provider lets the FIRST
 * mention of a given entity render the full image-chip and every subsequent
 * mention render as a plain entity link (no image) — the link survives, the
 * repetition dies.
 *
 * Mechanism — stable OWNER MAP (not a per-render reset):
 *   - The provider owns a `Map<entityKey, ownerInstanceId>` for the lifetime of
 *     the document render tree.
 *   - Each TagMention passes a stable `useId()` and a `ready` flag (true once its
 *     tag has resolved client-side). The FIRST mention to claim a key *while
 *     ready* becomes its permanent owner; that owner always gets `true`, everyone
 *     else gets `false` — idempotently, across re-renders.
 *
 * Why not "reset the set on every provider render"? TagMention resolves its tag
 * with a client-side fetch and re-renders INDEPENDENTLY of the provider. A
 * per-render reset only fires when the *provider* re-renders, so after the async
 * mounts settled, the original first-mention would find its key already claimed
 * (incl. from the skeleton pass) and return false — stripping the image off
 * EVERY mention (observed 2026-06-13). The owner-map + `ready` gate is immune to
 * that: pre-resolution claims are ignored, and ownership never moves once set.
 *
 * Outside a provider, `useMentionDedupe` always returns true (every mention is a
 * "first" mention) — i.e. the chip renders normally anywhere this isn't wired up.
 */

import React, { createContext, useContext, useRef } from 'react';

interface MentionDedupeValue {
  /**
   * Claim a handle/slug as "seen" for this document render.
   * @param key      resolved entity slug (so the same entity referenced by both
   *                 slug and twitter handle collapses to one)
   * @param ownerId  stable per-instance id (useId) — lets an owner re-claim
   *                 idempotently across its own async re-renders
   * @param ready    true once the tag has resolved; pre-resolution claims are
   *                 ignored (no image shows then anyway)
   * @returns true if this instance owns the first mention (render the image-chip),
   *          false if another instance already owns it (render plain link).
   */
  claimFirstMention: (key: string, ownerId: string, ready: boolean) => boolean;
}

const MentionDedupeContext = createContext<MentionDedupeValue | null>(null);

/** TagMention reads this to decide whether to show the image. */
export function useMentionDedupe(
  key: string,
  ownerId: string,
  ready: boolean,
): { isFirstMention: boolean } {
  const ctx = useContext(MentionDedupeContext);
  // No provider → treat every mention as first (chip renders as before).
  if (!ctx) return { isFirstMention: true };
  return { isFirstMention: ctx.claimFirstMention(key, ownerId, ready) };
}

export function MentionDedupeProvider({ children }: { children: React.ReactNode }) {
  const ownersRef = useRef<Map<string, string>>(new Map());

  const value = useRef<MentionDedupeValue>({
    claimFirstMention: (key: string, ownerId: string, ready: boolean) => {
      // Pre-resolution the tag is unknown and no image renders — don't let the
      // tentative raw-handle key grab ownership.
      if (!ready) return true;
      const normalized = (key || '').toLowerCase();
      if (!normalized || !ownerId) return true;
      const current = ownersRef.current.get(normalized);
      if (current === undefined) {
        ownersRef.current.set(normalized, ownerId);
        return true;
      }
      return current === ownerId;
    },
  }).current;

  return (
    <MentionDedupeContext.Provider value={value}>
      {children}
    </MentionDedupeContext.Provider>
  );
}
